import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../auth.service';
import { SvcAppSettings } from '@solvace/common/settings/svc-app-settings';
import { SvcModule } from '@solvace/common/settings/enums/svc-module.enum';
import { environment } from 'projects/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NoAuthGuard {
  /**
   * Constructor
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _appSettings: SvcAppSettings
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._check(state?.url);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._check(state?.url);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this._check().pipe(map((result) => result === true));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @private
   */
  private _check(targetUrl?: string): Observable<boolean | UrlTree> {
    // AB#73695 - /user-locked/* is for authenticated-but-restricted users (Rejected, Pending,
    // GDPR-pending, ...), never "no-auth" territory. Bouncing them away here while AuthGuard's own
    // locked-redirect independently pushes them right back produced a runaway back-button loop.
    if (targetUrl?.split('?')[0]?.includes('/user-locked')) {
      return of(true);
    }

    // Check the authentication status
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        // If the user is authenticated...
        if (authenticated) {
          // Redirect to the root
          if (this._appSettings.module === SvcModule.Authentication && !environment.isLocalhost) {
            return of(this._router.createUrlTree(['/signed-in-redirect']));
          } else if (this._appSettings.module === SvcModule.ExternalFeatures) {
            return of(this._router.createUrlTree(['/user/settings']));
          }
          return of(this._router.createUrlTree(['']));
        }

        // Allow the access
        return of(true);
      })
    );
  }
}
